import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useSearchParams } from 'react-router-dom';
import { resetPasswordSchema, type ResetPasswordFormData } from '@/schemas/auth';
import { post } from '@/lib/api-client';
import { AuthLayout } from '@/layouts/AuthLayout';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import type { ApiError } from '@/types/api';

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [success, setSuccess] = useState(false);
  const [apiError, setApiError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
  });

  if (!token) {
    return (
      <AuthLayout title="Invalid Link" subtitle="This reset link is missing or invalid.">
        <div className="text-center">
          <Link to="/forgot-password" className="text-sm text-accent-primary hover:underline">
            Request a new reset link
          </Link>
        </div>
      </AuthLayout>
    );
  }

  if (success) {
    return (
      <AuthLayout title="Password Reset" subtitle="Your password has been updated.">
        <div className="text-center">
          <Link to="/login" className="text-sm text-accent-primary hover:underline">
            Sign in with your new password
          </Link>
        </div>
      </AuthLayout>
    );
  }

  const onSubmit = async (data: ResetPasswordFormData) => {
    setApiError('');
    try {
      await post('/auth/reset-password', { token, password: data.password });
      setSuccess(true);
    } catch (err) {
      setApiError((err as ApiError).error || 'Failed to reset password');
    }
  };

  return (
    <AuthLayout title="Reset Password" subtitle="Enter your new password">
      {apiError && (
        <div role="alert" className="mb-4 rounded-md border border-accent-danger/30 bg-accent-danger/10 px-4 py-3 text-sm text-accent-danger">
          {apiError}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <Input
          label="New Password"
          type="password"
          autoComplete="new-password"
          error={errors.password?.message}
          helperText="Min 8 characters, 1 uppercase, 1 lowercase, 1 number"
          {...register('password')}
        />
        <Input
          label="Confirm Password"
          type="password"
          autoComplete="new-password"
          error={errors.confirmPassword?.message}
          {...register('confirmPassword')}
        />
        <Button type="submit" loading={isSubmitting} className="w-full">
          Reset Password
        </Button>
      </form>
    </AuthLayout>
  );
}
